import type { LucideIcon } from "lucide-react";
import { Card, CardContent } from "@component/card";
import { Tooltip } from "@component/tooltip";

export interface StatTileProps {
    label: string;
    value: string | number;
    /** Small muted text shown next to the value, e.g. "2/6". */
    badge?: string;
    /** Renders a skeleton in place of the value while data loads. */
    loading?: boolean;
    /** Optional leading icon, shown in a rounded blue tile. */
    icon?: LucideIcon;
    /** Optional hover tooltip explaining the metric. */
    tooltip?: string;
}

// Shared KPI card for the training area (dashboard + reports + employees).
// Matches the Application & Assessment Pipeline cards: a rounded blue icon
// tile on the left, an uppercase label, a bold value (+ optional badge), and
// a hover tooltip. Kept in one place so the cards stay visually in sync.
export function StatTile({
    label,
    value,
    badge,
    loading = false,
    icon: Icon,
    tooltip,
}: StatTileProps) {
    const card = (
        <Card className="h-full transition-all duration-200 hover:border-blue-500 hover:shadow-md">
            <CardContent className="p-4 min-h-[80px]">
                <div className="flex items-center gap-3">
                    {Icon && (
                        <div className="flex-shrink-0 w-10 h-10 flex items-center justify-center rounded-lg bg-blue-50">
                            <Icon className="h-5 w-5 text-blue-600" />
                        </div>
                    )}
                    <div className="min-w-0">
                        <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">{label}</div>
                        {loading ? (
                            <div className="mt-1 h-7 w-16 bg-gray-100 rounded animate-pulse" />
                        ) : (
                            <div className="flex items-baseline gap-2 flex-wrap">
                                <div className="text-2xl font-semibold text-gray-900 leading-none">{value}</div>
                                {badge && (
                                    <span className="text-xs font-medium text-gray-500">{badge}</span>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </CardContent>
        </Card>
    );

    if (tooltip) {
        return <Tooltip content={<p>{tooltip}</p>}>{card}</Tooltip>;
    }
    return card;
}
